import "@videojs/http-streaming";
import * as React from 'react';
import videojs, { VideoJsPlayer, VideoJsPlayerOptions } from 'video.js';
// Styles
import 'video.js/dist/video-js.css';

/////////////////////////////////////////////////////////////////////////////

export type VideJsPlayerEvent = (player: CommonPlayerApi) => void;

export interface CommonPlayerApi {
    duration: number,
    time: number,
    seeking: boolean;
    seek: (position: number) => void;
};

/////////////////////////////////////////////////////////////////////////////

interface IProps {
    id: string;
    className?: string;
    videoOptions: VideoJsPlayerOptions;
    onLoadedData?: VideJsPlayerEvent;
    onSeeking?: VideJsPlayerEvent;
    onSeeked?: VideJsPlayerEvent;
    onEnded?: VideJsPlayerEvent;
    onPause?: VideJsPlayerEvent;
    onPlaying?: VideJsPlayerEvent;
    onProgress?: VideJsPlayerEvent;
    onReady?: VideJsPlayerEvent;
    onPlayStartError?: (e: any) => void;
}

type Props = IProps;

class VideoJsCommonPlayer implements CommonPlayerApi {
    private player: VideoJsPlayer;

    constructor(player: VideoJsPlayer) {
        this.player = player;
    }

    get duration() {
        return this.player.duration();
    }

    get time() {
        return this.player.currentTime();
    }

    get seeking() {
        return this.player.seeking();
    }

    seek(position: number) {
        if (this.player.paused()) {
            this.player.currentTime(position);
        }
        else {
            this.player.pause();
            this.player.currentTime(position);
            this.player.play();
        }
    }
}

export default class VideoPlayer extends React.Component<Props> {
    private player?: VideoJsPlayer;
    private videoNode: HTMLVideoElement | null = null;

    componentDidMount() {
        this.player = videojs(this.videoNode, this.props.videoOptions, () => {

            const { onLoadedData, onSeeking, onEnded, onPause, onPlaying, onPlayStartError, onProgress, onReady, onSeeked, videoOptions: { autoplay } } = this.props;

            if (this.player != undefined) {
                const player: VideoJsPlayer = this.player;
                const cPlayer = new VideoJsCommonPlayer(this.player);

                if (this.videoNode.getAttribute("disablePictureInPicture") != undefined) {
                    const btn = document.getElementById(this.props.id).querySelector("button.vjs-picture-in-picture-control");
                    if (btn) btn.remove();
                }

                player.on("timeupdate", () => onProgress && onProgress(cPlayer));
                player.on("seeking", () => onSeeking && onSeeking(cPlayer));
                player.on("seeked", () => onSeeked && onSeeked(cPlayer));
                player.on("play", () => onPlaying && onPlaying(cPlayer));
                player.on("pause", () => onPause && onPause(cPlayer));
                player.on("ended", () => onEnded && onEnded(cPlayer));
                player.on("loadeddata", () => onLoadedData && onLoadedData(cPlayer));
                if (onReady) onReady(cPlayer);

                if (autoplay) {
                    var promise = player.play();
                    if (promise !== undefined) {
                        promise.then(function () {
                            // Autoplay started!
                        }).catch(function (error) {
                            if (onPlayStartError) onPlayStartError(error);
                        });
                    }
                }
            }
        });
    }

    // destroy player on unmount
    componentWillUnmount() {
        if (this.player) {
            this.player.off("timeupdate");
            this.player.off("seeking");
            this.player.off("seeked");
            this.player.off("play");
            this.player.off("pause");
            this.player.off("ended");
            this.player.off("loadeddata");
            this.player.dispose();
        }
    }

    // wrap the player in a div with a `data-vjs-player` attribute
    // so videojs won't create additional wrapper in the DOM
    // see https://github.com/videojs/video.js/pull/3856
    render() {
        return (
            <div className={this.props.className}>
                <div className="c-player" style={{ height: "100%" }}>
                    <div className="c-player__screen" data-vjs-player="true">
                        <video
                            id={this.props.id}
                            ref={node => this.videoNode = node}
                            className="video-js vjs-big-play-centered"
                            controlsList="nodownload"
                            disablePictureInPicture
                            style={{ height: "100%", width: "100%", maxWidth: "100%" }}
                        >
                        </video>
                    </div>
                </div>
            </div>
        );
    }
}

/////////////////////////////////////////////////////////////////////////////

export function stopAnothersVideoPlayers(currentPlayerId: string) {
    const players = document.getElementsByClassName("video-js");
    for (var i = 0; i < players.length; i++) {
        if (players[i].id != currentPlayerId) {
            const v = videojs(players[i].id);
            if (v) v.pause();
        }
    }
}